import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import { cn } from '@shared/lib/cn';
import { useLocalStorage } from '@shared/lib/useLocalStorage';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Breadcrumb } from './Breadcrumb';

/**
 * Coquille visuelle de l'application (Composite UI).
 *
 * Structure :
 *   ┌──────────────────────────── Header (pleine largeur) ─┐
 *   │ [toggle] logo + nom · breadcrumb                      │
 *   ├──────────┬────────────────────────────────────────────┤
 *   │ Sidebar  │  Contenu (Outlet)                          │
 *   └──────────┴────────────────────────────────────────────┘
 *
 * La sidebar est SOUS le header (pas d'intersection). Le bouton
 * d'ouverture/fermeture est dans le header : repli (desktop) et
 * tiroir superposé (mobile).
 */
export function AppLayout() {
  // Tiroir mobile (ouvert/fermé).
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Repli desktop (étendu / réduit), persisté entre les sessions.
  const [collapsed, setCollapsed] = useLocalStorage('receiptai.sidebar.collapsed', false);

  // Ferme le tiroir mobile à la touche Échap.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [sidebarOpen]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
      <Header
        collapsed={collapsed}
        onToggleSidebar={() => setCollapsed((value) => !value)}
        onOpenSidebar={() => setSidebarOpen(true)}
      />

      {/* Sous le header : sidebar + contenu */}
      <div className="flex min-h-0 flex-1">
        {/* Sidebar (desktop) — la transition porte sur le conteneur (largeur),
            pour que le contenu principal glisse de manière synchronisée. */}
        <div
          className={cn(
            'hidden shrink-0 transition-[width] duration-200 ease-in-out lg:block',
            collapsed ? 'w-16' : 'w-64',
          )}
        >
          {/* La sidebar reste fixe sous le header (h-16) au scroll. */}
          <div className="sticky top-16 h-[calc(100vh-4rem)] w-full">
            <Sidebar
              collapsed={collapsed}
              onToggleSidebar={() => setCollapsed((value) => !value)}
            />
          </div>
        </div>

        {/* Tiroir (mobile / tablette) — toujours monté, animé par glissement
            et fondu d'overlay pour une transition fluide. */}
        <div
          aria-hidden={!sidebarOpen}
          className={cn('fixed inset-0 z-40 lg:hidden', !sidebarOpen && 'pointer-events-none')}
        >
          {/* Overlay en fondu */}
          <button
            type="button"
            aria-label="Fermer le menu"
            tabIndex={sidebarOpen ? 0 : -1}
            onClick={() => setSidebarOpen(false)}
            className={cn(
              'absolute inset-0 bg-slate-900/50 transition-opacity duration-200 ease-in-out',
              sidebarOpen ? 'opacity-100' : 'opacity-0',
            )}
          />
          {/* Panneau coulissant */}
          <div
            className={cn(
              'absolute top-16 bottom-0 left-0 z-50 w-64 shadow-xl transition-transform duration-200 ease-in-out',
              sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            )}
          >
            <Sidebar onNavigate={() => setSidebarOpen(false)} />
          </div>
        </div>

        <main className="w-full min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-7">
          <div className="mx-auto w-full max-w-6xl">
            {/* Fil d'Ariane en haut de la page */}
            <div className="mb-4">
              <Breadcrumb />
            </div>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
